import { PageState } from '../app/PublicLayout.jsx'
import { assetUrl, useSite } from '../app/useSiteData.js'
import { parseContactEmail } from '../lib/format.js'
import { useI18n } from '../lib/i18n.jsx'
import { Container, DefinitionList, EmptyState, Media, PageHead, Section } from '../ui/index.js'

/**
 * Contact page.
 *
 * One screen, no hero, no form. A contact form on a research group's site is a
 * message queue nobody watches; an address, an email and a paragraph saying
 * concretely what to send do the same job and cannot silently drop anything.
 *
 * The "for prospective students" paragraph is the page's real content. It is
 * the group's own text (edited in the console, not shipped in the code),
 * because only the group knows what a prospective student should attach to
 * their first email.
 *
 * 联系页。
 *
 * 一屏、无主视觉、无表单。课题组网站上的联系表单是一个没人盯的消息队列；地址、邮箱，
 * 加上一段具体说明「该寄什么」的文字，做的是同一件事，而且不会悄无声息地丢东西。
 *
 * 「致有意报考的同学」那段才是本页真正的内容。它是课题组自己的文字（在后台编辑，
 * 不随代码发布），因为只有课题组知道有意报考的学生第一封邮件该附上什么。
 *
 * @returns {JSX.Element} Page / 页面。
 */
export default function Contact() {
  const { t, pick } = useI18n()
  const { data: site, loading, error, reload } = useSite()

  const address = site ? pick(site.address) : ''
  const joinNote = site ? pick(site.joinNote) : ''
  const qr = site ? assetUrl(site.wechatQr) : ''
  const people = Array.isArray(site?.contactPeople) ? site.contactPeople : []
  const mail = parseContactEmail(site?.email)

  // The affiliation is shown only when the group has opted in. Naming a
  // university can read as speaking for it, and that is the group's call to
  // make, not the page's.
  // 只有课题组选择公开时才显示所属单位。写出学校名字容易被读成「代表学校发言」，
  // 这件事应由课题组决定，而不是由页面决定。
  const showAffiliation = site?.showAffiliation !== false
  const institution = showAffiliation && site ? pick(site.institution) : ''
  const department = showAffiliation && site ? pick(site.department) : ''

  const facts = site
    ? [
        { label: t('contact.institution'), value: institution },
        { label: t('contact.department'), value: department },
        { label: t('contact.address'), value: address },
        {
          label: t('contact.email'),
          // The address goes in the href, the whole string stays visible. A
          // value like "a@b.com（某某）" therefore links correctly and still
          // reads as it was written.
          // 地址进 href，原串照常显示。像「a@b.com（某某）」这样的值因此既能正确
          // 跳转，又保持写下时的样子。
          value:
            mail.label &&
            (mail.address ? (
              <a className="contact-facts__mail" href={`mailto:${mail.address}`}>
                {mail.label}
              </a>
            ) : (
              mail.label
            )),
        },
        { label: t('contact.phone'), value: site.phone },
      ]
    : []

  const hasContent = Boolean(
    address || site?.email || site?.phone || joinNote || qr || people.length || institution
  )

  return (
    <Section className="contact-page">
      <Container width="narrow">
        <PageHead title={t('contact.title')} lede={t('contact.lede')} />

        <PageState
          loading={loading}
          error={error}
          onRetry={reload}
          isEmpty={!hasContent}
          empty={<EmptyState title={t('contact.title')} body={t('error.notFoundBody')} />}
        >
          <div className="contact-grid">
            <div className="contact-grid__main">
              <DefinitionList className="contact-facts" items={facts} />

              {/* Named people, so a visitor knows who reads the mail rather than
                  posting into a general inbox and hoping.
                  具名联系人，让访客知道信是谁在看，而不是投进公共信箱然后干等。 */}
              {people.length > 0 && (
                <section className="contact-people">
                  <h2 className="contact-people__heading">{t('contact.people')}</h2>
                  <ul className="contact-people__list">
                    {people.map((person, index) => {
                      const name = pick(person.name)
                      const role = pick(person.role)
                      if (!name && !person.email && !person.phone) return null
                      return (
                        <li className="contact-person" key={index}>
                          <p className="contact-person__name">{name}</p>
                          {role && <p className="contact-person__role">{role}</p>}
                          {person.email && (
                            <a className="contact-person__link" href={`mailto:${person.email}`}>
                              {person.email}
                            </a>
                          )}
                          {person.phone && <p className="contact-person__phone">{person.phone}</p>}
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )}
              {joinNote && (
                <section className="contact-join">
                  <h2 className="contact-join__heading">{t('contact.forStudents')}</h2>
                  <p className="contact-join__body">{joinNote}</p>
                </section>
              )}
            </div>

            {qr && (
              <aside className="contact-qr">
                <h2 className="contact-qr__heading">{t('contact.wechat')}</h2>
                <Media className="contact-qr__frame">
                  <img src={qr} alt={t('contact.wechat')} loading="lazy" decoding="async" />
                </Media>
              </aside>
            )}
          </div>
        </PageState>
      </Container>
    </Section>
  )
}
